<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.domains', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-8">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex gap-x-2 flex-wrap items-center mt-3" v-if="extraFilters.length">
            <BadgePill v-for="extraFilter in extraFilters" :key="extraFilter.key">
                <InertiaLink :href="extraFilter.url">
                    &times;
                    {{ extraFilter.text }}
                </InertiaLink>
            </BadgePill>
        </div>

        <div class="w-full flex space-x-4 justify-end">
            <ImportSyllabusPanel :import-syllabi="importSyllabi" v-if="importSyllabi.length" />
            <AppButton element="InertiaLink" :href="meta.links.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.stages', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.domains', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.subject', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.dimensions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(category, index) in categories.data"
                                :key="category.links.show"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ category.stage_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ category.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex gap-x-2">
                                        <span>
                                            {{ category.subjects_count }}
                                        </span>
                                        <span v-if="category.dimensions_count === 0">
                                            <Tippy>
                                                <InertiaLink :href="category.links.show">
                                                    <BadgePill color="red">
                                                        {{ $tc('messages.unfinished', 2) }}
                                                    </BadgePill>
                                                </InertiaLink>

                                                <template #content>
                                                    {{ category.subjects_missing_message }}
                                                </template>
                                            </Tippy>
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex gap-x-2">
                                        <span>
                                            {{ category.dimensions_count }}
                                        </span>
                                        <span v-if="category.dimensions_count === 0">
                                            <Tippy>
                                                <InertiaLink :href="category.links.show">
                                                    <BadgePill color="red">
                                                        {{ $tc('messages.unfinished', 2) }}
                                                    </BadgePill>
                                                </InertiaLink>

                                                <template #content>
                                                    {{ category.dimensions_missing_message }}
                                                </template>
                                            </Tippy>
                                        </span>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="category.links.show" />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="category.links.edit"
                                        v-if="category.links.edit"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="categories" />
        </template>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import ImportSyllabusPanel from '@/components/pageSpecific/ImportSyllabusPanel.vue';
import AppButton from '@/components/AppButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import { Tippy } from 'vue-tippy';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        BadgePill,
        AppButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        FormLabel,
        FormSelect,
        FormError,
        ImportSyllabusPanel,
        Tippy,
    },
    layout: AdminLayout,
    name: 'Index',
    props: ['meta', 'categories', 'filters', 'extraFilters', 'options', 'importSyllabi'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                school_year_id: this.filters.school_year_id,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
